import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save } from "lucide-react";

const TIMELINES = ["Just exploring", "3-6 months", "6-12 months", "12+ months"];
const FINANCING = ["Not started", "Talking to lenders", "Pre-qualified", "Pre-approved", "Paying cash"];
const PROPERTY = ["Single-family", "Townhouse", "Condo", "Multi-family", "Not sure yet"];

export const ClientProfileEditor = ({ clientId, profile }) => {
  const qc = useQueryClient();
  const p = profile || {};
  const [f, setF] = useState({
    budget_min: p.budget_min || "", budget_max: p.budget_max || "",
    areas: (p.areas || []).join(", "), timeline: p.timeline || "",
    financing_status: p.financing_status || "", must_haves: (p.must_haves || []).join(", "),
    nice_to_haves: (p.nice_to_haves || []).join(", "), household_size: p.household_size || "",
    property_type: p.property_type || "", lifestyle_notes: p.lifestyle_notes || "",
    deal_breakers: p.deal_breakers || "", comfort_level: p.comfort_level || "",
    questions: p.questions || "", workplace: p.workplace || "",
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        budget_min: f.budget_min ? Number(f.budget_min) : null,
        budget_max: f.budget_max ? Number(f.budget_max) : null,
        areas: f.areas.split(",").map((s) => s.trim()).filter(Boolean),
        timeline: f.timeline, financing_status: f.financing_status,
        must_haves: f.must_haves.split(",").map((s) => s.trim()).filter(Boolean),
        nice_to_haves: f.nice_to_haves.split(",").map((s) => s.trim()).filter(Boolean),
        household_size: f.household_size, property_type: f.property_type,
        lifestyle_notes: f.lifestyle_notes, deal_breakers: f.deal_breakers,
        comfort_level: f.comfort_level, questions: f.questions, workplace: f.workplace,
      };
      return (await api.put(`/agent/clients/${clientId}/profile`, payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", clientId] }); toast.success("Profile saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setV = (k) => (v) => setF({ ...f, [k]: v });

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">Enter what your client shared during their buyer consultation. They'll see this in their workspace — read-only — so they never have to type anything in.</p>

      <Card className="p-5 rounded-2xl space-y-5">
        <h3 className="font-heading font-semibold">Budget & timeline</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Target price (min)</Label>
            <Input type="number" value={f.budget_min} onChange={set("budget_min")} data-testid="cp-budget-min" placeholder="250000" className="h-11 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Target price (max)</Label>
            <Input type="number" value={f.budget_max} onChange={set("budget_max")} data-testid="cp-budget-max" placeholder="400000" className="h-11 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Timeline</Label>
            <Select value={f.timeline} onValueChange={setV("timeline")}>
              <SelectTrigger data-testid="cp-timeline" className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Financing status</Label>
            <Select value={f.financing_status} onValueChange={setV("financing_status")}>
              <SelectTrigger data-testid="cp-financing" className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{FINANCING.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="p-5 rounded-2xl space-y-5">
        <h3 className="font-heading font-semibold">What they're looking for</h3>
        <div className="space-y-1.5">
          <Label>Preferred areas <span className="text-muted-foreground font-normal">(comma separated)</span></Label>
          <Input value={f.areas} onChange={set("areas")} data-testid="cp-areas" placeholder="Kailua, Mililani, Kapolei" className="h-11 rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <Label>Workplace / commute address <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input value={f.workplace} onChange={set("workplace")} data-testid="cp-workplace" placeholder="e.g. 1000 Bishop St, Honolulu" className="h-11 rounded-xl" />
          <p className="text-xs text-muted-foreground">Powers the commute links from each home they tour or shortlist.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Must-haves <span className="text-muted-foreground font-normal">(comma separated)</span></Label>
          <Input value={f.must_haves} onChange={set("must_haves")} data-testid="cp-musthaves" placeholder="3 bedrooms, garage, backyard" className="h-11 rounded-xl" />
          <p className="text-xs text-muted-foreground">These become the criteria they score homes against.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Nice-to-haves</Label>
          <Input value={f.nice_to_haves} onChange={set("nice_to_haves")} data-testid="cp-nicetohaves" placeholder="Updated kitchen, home office" className="h-11 rounded-xl" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Household size (optional)</Label>
            <Input value={f.household_size} onChange={set("household_size")} data-testid="cp-household" placeholder="2 adults, 1 child" className="h-11 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Property type (optional)</Label>
            <Select value={f.property_type} onValueChange={setV("property_type")}>
              <SelectTrigger data-testid="cp-property" className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{PROPERTY.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Deal-breakers (optional)</Label>
          <Textarea value={f.deal_breakers} onChange={set("deal_breakers")} data-testid="cp-dealbreakers" placeholder="No busy roads, no major repairs needed" className="rounded-xl" />
        </div>
      </Card>

      <Card className="p-5 rounded-2xl space-y-5">
        <h3 className="font-heading font-semibold">Notes from the consultation</h3>
        <div className="space-y-1.5">
          <Label>Comfort level / anything on their mind</Label>
          <Textarea value={f.comfort_level} onChange={set("comfort_level")} data-testid="cp-comfort" placeholder="First time doing this and a little nervous about the process..." className="rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <Label>Open questions to revisit (optional)</Label>
          <Textarea value={f.questions} onChange={set("questions")} data-testid="cp-questions" className="rounded-xl" />
        </div>
      </Card>

      <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="cp-save-btn" className="h-12 rounded-full px-8 gap-2">
        {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save profile
      </Button>
    </div>
  );
};
